import { useState } from 'react';

import { Collapsable, CollapsableHeader, Icons } from '@/components';
import { useProjectDetails } from '../../hooks/use-project-details';
import { GeneralBox, GeneralNewDrawer } from './general';
import { SolarBox, SolarDrawer } from './solar';
import { WindBox, WindDrawer } from './wind';
import { BessBox, BessDrawer } from './bess';
import { HydrogenBox, HydrogenDrawer } from './hydrogen';
import { PermitNewDrawer, PermitsBox } from './permits';
import { LandRightsBox, LandRightsDrawerNew } from './land-rights';
import { ConnectionInfraDrawerNew, ConnectionInfrastructureBox } from './connection-infrastructure';
import { CapexOpexBox, CapexOpexDrawerNew } from './capex-opex';
import { OtherInfoDrawerNew, OtherInformationBox } from './other-information';
import { IconName } from '@/components/icons/types';
import { useBelongsToMyCompany } from '@/features/company/hooks';
import { useUpdateTechnology } from '../../hooks/use-update-technology';
import { useUpdateProject } from '../../hooks/use-update-project';
import { BiogasBox, BiogasDrawer } from './biogas';
import { HidropowerBox, HidropowerDrawer } from './hidropower';
import { DatacenterBox, DatacenterDrawer } from './datacenter';
import { TopcoBox, TopcoDrawer } from './topco';
import { PortfolioSummaryBox, PortfolioSummaryDrawer } from './portfolio-summary';
import { FinancialHighlightsBox, FinancialHighlightsDrawer } from './financial-highlights';
import { TeamGovernanceBox, TeamGovernanceDrawer } from './team-governance';

export type ProjectViewCollapsableProps = {
  readonly projectId: string;
  readonly opportunityId: string;
  readonly title: string;
  readonly isExpanded: boolean;
  readonly onToggle: () => void;
  readonly index?: number;
  readonly isPaid?: boolean;
};

export type IconState = {
  icon: IconName;
  status: 'active' | 'inactive';
};

export function ProjectViewCollapsable({
  projectId,
  opportunityId,
  title,
  isExpanded,
  index,
  onToggle,
  isPaid = false,
}: ProjectViewCollapsableProps) {
  const [drawer, setDrawer] = useState<string | null>(null);
  const { project, normalizedProject } = useProjectDetails(projectId);

  const projectBelongsToMyCompany = useBelongsToMyCompany(normalizedProject?.company);

  const { onSubmitItem } = useUpdateTechnology({
    projectId: projectId,
    opportunityId: opportunityId,
    setDrawer,
  });

  const { onSubmitProjectItem, deletePermitNew, onSubmitProject } = useUpdateProject({
    projectId: projectId,
    opportunityId: opportunityId,
    setDrawer,
  });

  const isEditable = !!(projectBelongsToMyCompany && normalizedProject?.isDraft);

  if (!normalizedProject) return null;
  if (!project) return null;

  const techTypes = [...new Set(normalizedProject.technologies.map((t: any) => t.type))];
  const technologyIcons: IconState[] = techTypes.map((type) => ({
    icon: type,
    status: 'active',
  }));

  const solarTechnology = project.technologies.find((t) => t.collection === 'projects_solar_technologies');
  const windTechnology = project.technologies.find((t) => t.collection === 'projects_wind_technologies');
  const bessTechnology = project.technologies.find((t) => t.collection === 'projects_bess_technologies');
  const hydrogenTechnology = project.technologies.find((t) => t.collection === 'projects_hydrogen_technologies');
  const biogasTechnology = project.technologies.find((t) => t.collection === 'projects_biogas_technologies');
  const hidropowerTechnology = project.technologies.find((t) => t.collection === 'projects_hidropower_technologies');
  const datacenterTechnology = project.technologies.find((t) => t.collection === 'projects_datacenter_technologies');
  const topcoTechnology = project.technologies.find((t) => t.collection === 'projects_topco_technologies');

  const isTopco = Boolean(topcoTechnology);
  return (
    <Collapsable
      index={index}
      isExpanded={isExpanded}
      header={
        <CollapsableHeader
          title={title}
          isExpanded={isExpanded}
          onToggle={onToggle}
          weight="semibold"
          headClasses="text-[22px]"
          icons={<Icons size="lg" icons={technologyIcons} className="mr-2" />}
        />
      }
    >
      <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
        {!topcoTechnology && (
          <>
            <GeneralBox
              normalizedProject={normalizedProject}
              onEdit={isEditable ? () => setDrawer(`general-form-${project.id}`) : undefined}
              isEditable={isEditable}
            />
            <GeneralNewDrawer
              project={project}
              formId={`general-form-${project.id}`}
              onSubmit={(data, description) => onSubmitProjectItem(project.id, data, description)}
              isOpen={drawer == `general-form-${project.id}`}
              onClose={() => setDrawer(null)}
            />
          </>
        )}

        <SolarBox
          normalizedProject={normalizedProject}
          onEdit={isEditable ? () => setDrawer(`solar-form-${project.id}`) : undefined}
          isEditable={isEditable}
        />
        {solarTechnology && (
          <SolarDrawer
            formId={`solar-form-${project.id}`}
            technology={solarTechnology}
            isOpen={drawer === `solar-form-${project.id}`}
            onClose={() => setDrawer(null)}
            onSubmit={(collection, data, description) => onSubmitItem(collection, data, description)}
          />
        )}

        <WindBox
          normalizedProject={normalizedProject}
          isEditable={isEditable}
          onEdit={isEditable ? () => setDrawer(`wind-form-${project.id}`) : undefined}
        />
        {windTechnology && (
          <WindDrawer
            formId={`wind-form-${project.id}`}
            technology={windTechnology}
            isOpen={drawer == `wind-form-${project.id}`}
            onClose={() => setDrawer(null)}
            onSubmit={(collection, data, description) => onSubmitItem(collection, data, description)}
          />
        )}

        <BessBox
          normalizedProject={normalizedProject}
          isEditable={isEditable}
          onEdit={isEditable ? () => setDrawer(`bess-form-${project.id}`) : undefined}
        />
        {bessTechnology && (
          <BessDrawer
            formId={`bess-form-${project.id}`}
            technology={bessTechnology}
            isOpen={drawer == `bess-form-${project.id}`}
            onClose={() => setDrawer(null)}
            onSubmit={(collection, data, description) => onSubmitItem(collection, data, description)}
          />
        )}

        <BiogasBox
          normalizedProject={normalizedProject}
          isEditable={isEditable}
          onEdit={isEditable ? () => setDrawer(`biogas-form-${project.id}`) : undefined}
        />
        {biogasTechnology && (
          <BiogasDrawer
            formId={`biogas-form-${project.id}`}
            technology={biogasTechnology}
            isOpen={drawer == `biogas-form-${project.id}`}
            onClose={() => setDrawer(null)}
            onSubmit={(collection, data, description) => onSubmitItem(collection, data, description)}
          />
        )}

        <HydrogenBox
          normalizedProject={normalizedProject}
          isEditable={isEditable}
          onEdit={isEditable ? () => setDrawer(`hydrogen-form-${project.id}`) : undefined}
        />
        {hydrogenTechnology && (
          <HydrogenDrawer
            formId={`hydrogen-form-${project.id}`}
            technology={hydrogenTechnology}
            isOpen={drawer == `hydrogen-form-${project.id}`}
            onClose={() => setDrawer(null)}
            onSubmit={(collection, data, description) => onSubmitItem(collection, data, description)}
          />
        )}

        <HidropowerBox
          normalizedProject={normalizedProject}
          isEditable={isEditable}
          onEdit={isEditable ? () => setDrawer(`hidropower-form-${project.id}`) : undefined}
        />
        {hidropowerTechnology && (
          <HidropowerDrawer
            formId={`hidropower-form-${project.id}`}
            technology={hidropowerTechnology}
            isOpen={drawer == `hidropower-form-${project.id}`}
            onClose={() => setDrawer(null)}
            onSubmit={(collection, data, description) => onSubmitItem(collection, data, description)}
          />
        )}

        <DatacenterBox
          normalizedProject={normalizedProject}
          isEditable={isEditable}
          onEdit={isEditable ? () => setDrawer(`datacenter-form-${project.id}`) : undefined}
        />
        {datacenterTechnology && (
          <DatacenterDrawer
            formId={`datacenter-form-${project.id}`}
            technology={datacenterTechnology}
            isOpen={drawer == `datacenter-form-${project.id}`}
            onClose={() => setDrawer(null)}
            onSubmit={(collection, data, description) => onSubmitItem(collection, data, description)}
          />
        )}

        <TopcoBox
          normalizedProject={normalizedProject}
          isEditable={isEditable}
          onEdit={isEditable ? () => setDrawer(`topco-form-${project.id}`) : undefined}
        />
        {topcoTechnology && (
          <TopcoDrawer
            formId={`topco-form-${project.id}`}
            technology={topcoTechnology}
            isOpen={drawer == `topco-form-${project.id}`}
            onClose={() => setDrawer(null)}
            onSubmit={(collection, data, description) => onSubmitItem(collection, data, description)}
          />
        )}

        {!isTopco && (
          <>
            <PermitsBox
              normalizedProject={normalizedProject}
              country={project?.country}
              isEditable={isEditable}
              onEdit={isEditable ? () => setDrawer(`permits-form-${project.id}`) : undefined}
            />
            <PermitNewDrawer
              formId={`permits-form-${project.id}`}
              project={project}
              onSubmit={(data, description) => onSubmitProjectItem(project.id, data, description)}
              isOpen={drawer == `permits-form-${project.id}`}
              onClose={() => setDrawer(null)}
              country={project?.country}
              deletePermitNew={deletePermitNew}
            />

            <ConnectionInfrastructureBox
              normalizedProject={normalizedProject}
              isEditable={isEditable}
              onEdit={isEditable ? () => setDrawer(`connection-form-${project.id}`) : undefined}
            />
            <ConnectionInfraDrawerNew
              formId={`connection-form-${project.id}`}
              project={project}
              onSubmit={(data, description) => onSubmitProjectItem(project.id, data, description)}
              isOpen={drawer == `connection-form-${project.id}`}
              onClose={() => setDrawer(null)}
            />

            <LandRightsBox
              normalizedProject={normalizedProject}
              isEditable={isEditable}
              onEdit={isEditable ? () => setDrawer(`land-rights-form-${project.id}`) : undefined}
              isDataCenterTechnology={Boolean(datacenterTechnology)}
            />
            <LandRightsDrawerNew
              formId={`land-rights-form-${project.id}`}
              project={project}
              onSubmit={(data, description) => onSubmitProjectItem(project.id, data, description)}
              isOpen={drawer == `land-rights-form-${project.id}`}
              onClose={() => setDrawer(null)}
            />

            <CapexOpexBox
              normalizedProject={normalizedProject}
              isEditable={isEditable}
              onEdit={isEditable ? () => setDrawer(`capex-opex-form-${project.id}`) : undefined}
            />
            <CapexOpexDrawerNew
              formId={`capex-opex-form-${project.id}`}
              project={project}
              onSubmit={(data, description) => onSubmitProjectItem(project.id, data, description)}
              isOpen={drawer == `capex-opex-form-${project.id}`}
              onClose={() => setDrawer(null)}
            />

            <OtherInformationBox
              normalizedProject={normalizedProject}
              technology={normalizedProject.technologies}
              isEditable={isEditable}
              onEdit={isEditable ? () => setDrawer(`other-info-form-${project.id}`) : undefined}
            />
            <OtherInfoDrawerNew
              formId={`other-info-form-${project.id}`}
              project={project}
              onSubmit={(data, description) => onSubmitProject(data, description)}
              isOpen={drawer == `other-info-form-${project.id}`}
              onClose={() => setDrawer(null)}
            />
          </>
        )}
        {isTopco && (
          <>
            <PortfolioSummaryBox
              normalizedProject={normalizedProject}
              isEditable={isEditable}
              onEdit={isEditable ? () => setDrawer(`portfolio-summary-form-${project.id}`) : undefined}
            />
            <PortfolioSummaryDrawer
              formId={`portfolio-summary-form-${project.id}`}
              project={project}
              onSubmit={(data, description) => onSubmitProjectItem(project.id, data, description)}
              isOpen={drawer == `portfolio-summary-form-${project.id}`}
              onClose={() => setDrawer(null)}
            />

            <FinancialHighlightsBox
              normalizedProject={normalizedProject}
              isEditable={isEditable}
              onEdit={isEditable ? () => setDrawer(`financial-highlights-form-${project.id}`) : undefined}
            />
            <FinancialHighlightsDrawer
              formId={`financial-highlights-form-${project.id}`}
              project={project}
              onSubmit={(data, description) => onSubmitProjectItem(project.id, data, description)}
              isOpen={drawer == `financial-highlights-form-${project.id}`}
              onClose={() => setDrawer(null)}
            />

            <TeamGovernanceBox
              normalizedProject={normalizedProject}
              isEditable={isEditable}
              onEdit={isEditable ? () => setDrawer(`team-governance-form-${project.id}`) : undefined}
            />
            <TeamGovernanceDrawer
              formId={`team-governance-form-${project.id}`}
              project={project}
              onSubmit={(data, description) => onSubmitProjectItem(project.id, data, description)}
              isOpen={drawer == `team-governance-form-${project.id}`}
              onClose={() => setDrawer(null)}
            />
          </>
        )}
      </div>
    </Collapsable>
  );
}
